import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store';

import { calculateCommission } from 'utils/analytics';

export const selectAnalytics = (state: RootState) => state.analytics;

export const selectAnalyticsCommission = createSelector(
  selectAnalytics,
  analytics => analytics.commission
);

export const selectAnalyticsOrganizationsCommissions = createSelector(
  selectAnalytics,
  analytics => analytics.organizationsCommissions
);

export const selectAnalyticsLoading = createSelector(
  selectAnalytics,
  analytics => analytics.loading
);

export const selectAnalyticsAnalytics = (organization: string) =>
  createSelector(
    [
      selectAnalytics,
      selectAnalyticsOrganizationsCommissions,
      selectAnalyticsCommission
    ],
    (analytics, organizationsCommissions, commission) => {
      const revenue = Object.entries(analytics.revenuePrice).reduce(
        (acc, [key, value]) =>
          acc +
          calculateCommission({
            commission,
            organizationsCommissions,
            organization
          })({
            key,
            value
          }),
        0
      );

      return { ...analytics.analytics, revenue: revenue.toString() };
    }
  );

export const selectAnalyticsRevenuePrice = (organization: string) =>
  createSelector(
    [
      selectAnalytics,
      selectAnalyticsOrganizationsCommissions,
      selectAnalyticsCommission
    ],
    (analytics, organizationsCommissions, commission) =>
      Object.entries(analytics.revenuePrice).reduce(
        (acc, [key, value]) => {
          acc[key as keyof typeof acc] = calculateCommission({
            commission,
            organizationsCommissions,
            organization
          })({
            key,
            value
          });

          return acc;
        },
        { ...analytics.revenuePrice }
      )
  );

export const selectAnalyticsRevenueQuantity = createSelector(
  selectAnalytics,
  analytics => analytics.revenueQuantity
);

export const selectAnalyticsDynamics = (organization: string) =>
  createSelector(
    [
      selectAnalytics,
      selectAnalyticsOrganizationsCommissions,
      selectAnalyticsCommission
    ],
    (analytics, organizationsCommissions, commission) => {
      const calculate = calculateCommission({
        commission,
        organizationsCommissions,
        organization
      });

      return analytics.dynamics.map(item => ({
        ...item,
        revenue: Object.entries({
          subscription_monthly: item.subscription_monthly_price,
          subscription_yearly: item.subscription_yearly_price,
          video_consultation: item.video_consultation_price,
          radiology: item.radiology_price
        }).reduce(
          (acc, [key, value]) =>
            acc +
            calculate({
              key,
              value
            }),
          0
        ),
        video_consultation_price: calculate({
          key: 'video_consultation',
          value: item.video_consultation_price
        }),
        radiology_price: calculate({
          key: 'radiology',
          value: item.radiology_price
        }),
        subscription_monthly_price: calculate({
          key: 'subscription_monthly',
          value: item.subscription_monthly_price
        }),
        subscription_yearly_price: calculate({
          key: 'subscription_yearly',
          value: item.subscription_yearly_price
        })
      }));
    }
  );

export const selectAnalyticsUtmValues = createSelector(
  selectAnalytics,
  analytics => analytics.utmValues
);

export const selectAnalyticsUtmValuesLoading = createSelector(
  selectAnalyticsUtmValues,
  utmValues => utmValues.loading
);

export const selectAnalyticsUtmValuesData = createSelector(
  selectAnalyticsUtmValues,
  utmValues => utmValues.data
);
